import { useCallback, useMemo, useRef, useState, type ChangeEvent, type FocusEvent, type MutableRefObject, type ReactNode } from 'react';
import type { Options } from '../../select/shared/normalize-options';
import type { TypeaheadMatchFn } from '../../select/typeahead/hooks/use-typeahead-filtering';
import type { DropdownCloseStrategy, TypeaheadDropdownApi, TypeaheadInteractionContext, TypeaheadValidationStrategy } from '../../select/typeahead/types';
import { mapDawaSuggestionToOption, type AddressFieldOptionMeta } from '../utils';
import { useDawaAutocomplete, type DawaSuggestion } from './use-dawa-autocomplete';
import { useAddressFieldEvents } from './use-address-field-events';
import type { AddressFieldEvent, AddressFieldStatus } from '../types';

type UseDawaAddressAdapterParams = {
    fieldEvent: AddressFieldEvent;
    dropdownApiRef?: MutableRefObject<TypeaheadDropdownApi | null>;
    displayInputRef?: MutableRefObject<HTMLInputElement | null>;
    allowCustomAddress?: boolean;
    renderCustomOptionLabel?: (value: string) => ReactNode;
};

type AddressOptionMeta = AddressFieldOptionMeta<DawaSuggestion>;
type AddressOptions = Options<AddressOptionMeta>;

type UseDawaAddressAdapterResult = {
    options: AddressOptions;
    matchOption: TypeaheadMatchFn;
    isLoading: boolean;
    status: AddressFieldStatus;
    kvhx?: string;
    kvhxUrl?: string;
    selectedSuggestion?: DawaSuggestion;
    selectedCustomValue?: string;
    selectedOptionValue?: string;
    validationStrategy: TypeaheadValidationStrategy;
    dropdownCloseStrategy: DropdownCloseStrategy;
    shouldAutoOpen: (value: string) => boolean;
    shouldOpenOnFocus: boolean;
    handleInputChange: (value: string) => void;
    handleHiddenInputChange: (event: ChangeEvent<HTMLInputElement>) => void;
    handleFocus: () => void;
    handleBlur: (event: FocusEvent<HTMLInputElement>) => void;
    reset: () => void;
    dawaQuery: string;
    interactionContext: TypeaheadInteractionContext;
};

const MIN_QUERY_LENGTH = 3;

const alwaysMatch: TypeaheadMatchFn = () => true;

const defaultRenderCustomOptionLabel = (value: string): ReactNode => `Brug "${value}"`;

const createValidationStrategy = ({
    allowCustomAddress,
    getSelectedSuggestion,
    getSelectedCustomValue,
}: {
    allowCustomAddress: boolean;
    getSelectedSuggestion: () => DawaSuggestion | undefined;
    getSelectedCustomValue: () => string | undefined;
}): TypeaheadValidationStrategy => {
    return (value) => {
        const trimmedValue = value.trim();
        if (!trimmedValue) {
            return { status: 'invalid', hiddenValue: '' };
        }

        const selected = getSelectedSuggestion();
        if (selected && trimmedValue === selected.label.trim()) {
            return { status: 'valid', hiddenValue: selected.id };
        }

        const customValue = getSelectedCustomValue();
        if (customValue && trimmedValue === customValue) {
            return { status: 'valid', hiddenValue: customValue };
        }

        if (allowCustomAddress) {
            return { status: 'valid', hiddenValue: trimmedValue };
        }

        return { status: 'invalid', hiddenValue: '' };
    };
};

const useDawaAddressAdapter = ({
    fieldEvent,
    dropdownApiRef,
    allowCustomAddress = false,
    renderCustomOptionLabel,
    displayInputRef,
}: UseDawaAddressAdapterParams): UseDawaAddressAdapterResult => {
    const dawa = useDawaAutocomplete();
    const { emitValid, emitInvalid, emitCustom } = useAddressFieldEvents(fieldEvent);

    const suggestionRegistryRef = useRef(new Map<string, DawaSuggestion>());
    const selectionRequestRef = useRef(0);

    const [selectedSuggestion, setSelectedSuggestion] = useState<DawaSuggestion | undefined>(undefined);
    const [selectedCustomValue, setSelectedCustomValue] = useState<string | undefined>(undefined);
    const [kvhxState, setKvhxState] = useState<string | undefined>(undefined);
    const [kvhxUrl, setKvhxUrl] = useState<string | undefined>(undefined);
    const [status, setStatus] = useState<AddressFieldStatus>(undefined);
    const kvhxRef = useRef<string | undefined>(undefined);
    const shouldRefetchOnFocusRef = useRef(false);

    const setKvhx = useCallback((next: string | undefined) => {
        kvhxRef.current = next;
        setKvhxState(next);
    }, []);

    const resolveCustomOptionLabel = useCallback(
        (rawValue: string) => {
            const trimmedValue = rawValue.trim();
            const labelBuilder = renderCustomOptionLabel ?? defaultRenderCustomOptionLabel;
            return labelBuilder(trimmedValue);
        },
        [renderCustomOptionLabel]
    );

    const kvhx = kvhxState;

    const options = useMemo<AddressOptions>(() => {
        const registry = suggestionRegistryRef.current;
        registry.clear();

        const suggestionOptions = dawa.suggestions.map((suggestion) => {
            registry.set(suggestion.id, suggestion);
            return mapDawaSuggestionToOption({
                ...suggestion,
                value: suggestion.id,
            });
        });

        if (selectedSuggestion && !registry.has(selectedSuggestion.id)) {
            registry.set(selectedSuggestion.id, selectedSuggestion);
            suggestionOptions.unshift(mapDawaSuggestionToOption({ ...selectedSuggestion, value: selectedSuggestion.id }));
        }

        const trimmedQuery = (dawa.query ?? '').trim();
        if (!allowCustomAddress || !trimmedQuery) {
            return suggestionOptions;
        }

        if (dawa.isLoading && suggestionOptions.length === 0) {
            return suggestionOptions;
        }

        const loweredQuery = trimmedQuery.toLowerCase();
        const hasExactMatch = suggestionOptions.some((option) => {
            const optionLabel = option.label ?? option.value;
            return optionLabel.trim().toLowerCase() === loweredQuery;
        });
        if (hasExactMatch) {
            return suggestionOptions;
        }

        const displayLabel = resolveCustomOptionLabel(trimmedQuery);
        const customOption = {
            value: trimmedQuery,
            label: trimmedQuery,
            meta: {
                kind: 'custom',
                rawValue: trimmedQuery,
                displayLabel,
                closeBehavior: 'close',
            } satisfies AddressOptionMeta,
        };

        return [customOption, ...suggestionOptions];
    }, [allowCustomAddress, dawa.isLoading, dawa.query, dawa.suggestions, resolveCustomOptionLabel, selectedSuggestion]);

    const dropdownCloseStrategy = useCallback<DropdownCloseStrategy>(({ option }) => {
        const meta = option.meta as AddressOptionMeta | undefined;
        return meta?.closeBehavior ?? 'keep-open';
    }, []);

    const handleFocus = useCallback(() => {
        setStatus(undefined);
        if (shouldRefetchOnFocusRef.current) {
            const trimmed = (dawa.query ?? '').trim();
            if (trimmed.length >= MIN_QUERY_LENGTH) {
                dawa.refetch();
                dropdownApiRef?.current?.open();
            } else if (allowCustomAddress && trimmed.length > 0) {
                dropdownApiRef?.current?.open();
            }
            shouldRefetchOnFocusRef.current = false;
        }
    }, [allowCustomAddress, dawa, dropdownApiRef]);

    const handleInputChange = useCallback(
        (value: string) => {
            const normalizedValue = value ?? '';
            const trimmed = normalizedValue.trim();

            dawa.setQuery(normalizedValue);

            if (!trimmed) {
                setSelectedSuggestion(undefined);
                setSelectedCustomValue(undefined);
                setStatus(undefined);
                emitInvalid('blur', kvhxRef.current);
                dropdownApiRef?.current?.close();
                dawa.reset();
                setKvhx(undefined);
                setKvhxUrl(undefined);
                shouldRefetchOnFocusRef.current = false;
                return;
            }

            if (kvhx) {
                setKvhx(undefined);
                setKvhxUrl(undefined);
            }

            setSelectedSuggestion(undefined);
            setSelectedCustomValue(undefined);
            setStatus(undefined);
            shouldRefetchOnFocusRef.current = false;

            if (trimmed.length < MIN_QUERY_LENGTH) {
                dawa.reset();
                if (allowCustomAddress) {
                    dropdownApiRef?.current?.open();
                } else {
                    dropdownApiRef?.current?.close();
                }
            } else {
                dropdownApiRef?.current?.open();
            }
        },
        [allowCustomAddress, dropdownApiRef, dawa, emitInvalid, kvhx, setKvhx, setKvhxUrl]
    );

    const alignDisplayCaret = useCallback(() => {
        if (!displayInputRef?.current) return;
        const run = () => {
            const node = displayInputRef.current;
            if (!node) return;
            const length = node.value.length;
            try {
                node.setSelectionRange(length, length);
            } catch (error) {
                // setSelectionRange may fail for unsupported input types; ignore in that case.
            }
        };
        setTimeout(run, 0);
    }, [displayInputRef]);

    const handleHiddenInputChange = useCallback(
        async (event: ChangeEvent<HTMLInputElement>) => {
            const nextValue = event.target.value;
            selectionRequestRef.current += 1;
            const requestId = selectionRequestRef.current;

            if (!nextValue) {
                setSelectedSuggestion(undefined);
                setSelectedCustomValue(undefined);
                setKvhx(undefined);
                setKvhxUrl(undefined);
                setStatus((previous) => (previous === 'invalid' ? previous : undefined));
                shouldRefetchOnFocusRef.current = false;
                const hasQuery = Boolean((dawa.query ?? '').trim());
                if (!hasQuery) {
                    const scheduleClose = () => dropdownApiRef?.current?.close();
                    if (typeof queueMicrotask === 'function') {
                        queueMicrotask(scheduleClose);
                    } else {
                        setTimeout(scheduleClose, 0);
                    }
                    dawa.reset();
                }
                return;
            }

            const suggestion = suggestionRegistryRef.current.get(nextValue);
            if (!suggestion) {
                if (!allowCustomAddress) {
                    return;
                }
                const trimmedCustomValue = nextValue.trim();
                if (!trimmedCustomValue) {
                    return;
                }

                setSelectedSuggestion(undefined);
                setSelectedCustomValue(trimmedCustomValue);
                setStatus('custom');
                setKvhx(undefined);
                setKvhxUrl(undefined);
                shouldRefetchOnFocusRef.current = false;
                dropdownApiRef?.current?.close();
                dawa.reset();
                dawa.setQuery(trimmedCustomValue);
                emitCustom('select', trimmedCustomValue);
                alignDisplayCaret();
                return;
            }

            const { enrichment } = await dawa.selectSuggestion(suggestion);
            if (selectionRequestRef.current !== requestId) return;

            const displayValue = suggestion.label;
            dawa.setQuery(displayValue);
            alignDisplayCaret();

            const nextKvhx = enrichment?.kvhx;
            setKvhx(nextKvhx);
            setKvhxUrl(suggestion.href);
            setSelectedCustomValue(undefined);
            if (nextKvhx) {
                setSelectedSuggestion(suggestion);
                setStatus('valid');
                dropdownApiRef?.current?.close();
                shouldRefetchOnFocusRef.current = false;
            } else {
                setSelectedSuggestion(undefined);
                setStatus(undefined);
                dropdownApiRef?.current?.open();
            }

            emitValid('select', suggestion, enrichment?.kvhx);
        },
        [alignDisplayCaret, allowCustomAddress, dawa, dropdownApiRef, emitCustom, emitValid, setKvhx, setKvhxUrl]
    );

    const handleBlur = useCallback(
        (event: FocusEvent<HTMLInputElement>) => {
            const relatedTarget = event.relatedTarget;
            if (relatedTarget instanceof Node) {
                const wrapper = event.currentTarget.closest('.typeaheadfield');
                if (wrapper && wrapper.contains(relatedTarget)) {
                    return;
                }
            }
            if (relatedTarget instanceof HTMLElement && relatedTarget.getAttribute('role') === 'option') {
                return;
            }
            const trimmedValue = event.currentTarget.value.trim();
            if (selectedSuggestion && trimmedValue === selectedSuggestion.label.trim()) {
                setStatus('valid');
                emitValid('blur', selectedSuggestion, kvhxRef.current);
                shouldRefetchOnFocusRef.current = false;
            } else if (selectedCustomValue && trimmedValue === selectedCustomValue) {
                setStatus('custom');
                emitCustom('blur', selectedCustomValue);
                shouldRefetchOnFocusRef.current = false;
            } else if (allowCustomAddress && trimmedValue) {
                setSelectedSuggestion(undefined);
                setSelectedCustomValue(trimmedValue);
                setStatus('custom');
                emitCustom('blur', trimmedValue);
                shouldRefetchOnFocusRef.current = false;
                dropdownApiRef?.current?.close();
            } else {
                setStatus('invalid');
                setSelectedSuggestion(undefined);
                setSelectedCustomValue(undefined);
                emitInvalid('blur', kvhxRef.current);
                shouldRefetchOnFocusRef.current = trimmedValue.length >= MIN_QUERY_LENGTH && !kvhxRef.current;
            }
            dawa.abort();
        },
        [allowCustomAddress, dawa, dropdownApiRef, emitCustom, emitInvalid, emitValid, selectedCustomValue, selectedSuggestion]
    );

    const reset = useCallback(() => {
        const previousKvhx = kvhxRef.current;
        setSelectedSuggestion(undefined);
        setSelectedCustomValue(undefined);
        setStatus(undefined);
        dawa.reset();
        emitInvalid('blur', previousKvhx);
        setKvhx(undefined);
        setKvhxUrl(undefined);
        shouldRefetchOnFocusRef.current = false;
        dropdownApiRef?.current?.close();
    }, [dawa, dropdownApiRef, emitInvalid, setKvhx, setKvhxUrl]);

    const shouldAutoOpen = useCallback(
        (value: string) => {
            const trimmed = (value ?? '').trim();
            if (!trimmed) return false;
            if (kvhx) return false;
            if (selectedCustomValue) {
                if (trimmed === selectedCustomValue) {
                    return false;
                }
                return trimmed.length < MIN_QUERY_LENGTH ? allowCustomAddress : true;
            }
            if (trimmed.length < MIN_QUERY_LENGTH) {
                return allowCustomAddress;
            }
            return true;
        },
        [allowCustomAddress, kvhx, selectedCustomValue]
    );

    const shouldOpenOnFocus = useMemo(() => {
        const trimmed = (dawa.query ?? '').trim();
        if (!trimmed) return false;
        if (kvhx) return false;
        if (selectedCustomValue) {
            return allowCustomAddress;
        }
        if (trimmed.length < MIN_QUERY_LENGTH) {
            return allowCustomAddress;
        }
        return true;
    }, [allowCustomAddress, dawa.query, kvhx, selectedCustomValue]);

    const validationStrategy = useMemo(
        () =>
            createValidationStrategy({
                allowCustomAddress,
                getSelectedSuggestion: () => selectedSuggestion,
                getSelectedCustomValue: () => selectedCustomValue,
            }),
        [allowCustomAddress, selectedCustomValue, selectedSuggestion]
    );

    const interactionContext = useMemo<TypeaheadInteractionContext>(
        () => ({
            onCommit: () => {
                // Hidden input change handler performs the heavy lifting; nothing extra required here.
            },
            onInteractionStart: () => {
                // Reserved for future interaction-specific logic.
            },
            onInteractionEnd: () => {
                // Reserved for future interaction-specific logic.
            },
            onInputChange: handleInputChange,
        }),
        [handleInputChange]
    );

    const selectedOptionValue = selectedSuggestion?.id ?? selectedCustomValue;

    return {
        options,
        matchOption: alwaysMatch,
        isLoading: dawa.isLoading,
        status,
        kvhx,
        kvhxUrl,
        selectedSuggestion,
        selectedCustomValue,
        selectedOptionValue,
        validationStrategy,
        dropdownCloseStrategy,
        shouldAutoOpen,
        shouldOpenOnFocus,
        handleInputChange,
        handleHiddenInputChange,
        handleFocus,
        handleBlur,
        reset,
        dawaQuery: dawa.query,
        interactionContext,
    };
};

export type { UseDawaAddressAdapterParams, UseDawaAddressAdapterResult };
export { useDawaAddressAdapter };
